/**
 * iter322 — GMP Evidence Attachments reusable panel.
 *
 * Props:
 *   module: "capa" | "deviation" | "complaint" | "oos" | "internal_audit" |
 *           "risk" | "recall" | "waste"
 *   recordId: string
 *   title?: string (default "Ekler / Kanıtlar")
 *
 * Uses existing /api/gmp/evidence/{module}/{record_id} endpoints.
 * All users: upload + preview + download. Admin/system_authority: delete.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/auth";
import { api } from "@/lib/api";
import { Btn } from "@/components/UI";
import { Download, Eye, FileText, Loader2, Trash2, Upload } from "lucide-react";

const KIND_LABELS = {
  photo: "Fotoğraf",
  lab_result: "Laboratuvar Sonucu",
  signed_form: "İmzalı Form",
  supplier_doc: "Tedarikçi Belgesi",
  customer_doc: "Müşteri Belgesi",
  correspondence: "Yazışma",
  corrective_evidence: "Düzeltici Faaliyet Kanıtı",
  training_record: "Eğitim/Kayıt",
  distribution_proof: "Dağıtım/Teslim Kanıtı",
  other: "Diğer",
};

export default function GmpEvidencePanel({ module, recordId, title }) {
  const [data, setData] = useState({ attachments: [], count: 0, can_manage: false });
  const [kind, setKind] = useState("other");
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/gmp/evidence/${module}/${recordId}`);
      setData(r);
    } catch { /* parent may not exist yet */ }
  }, [module, recordId]);

  useEffect(() => { if (recordId) load(); }, [recordId, load]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      if (desc) form.append("description", desc);
      await axios.post(`${API}/gmp/evidence/${module}/${recordId}`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });
      setFile(null); setDesc(""); setKind("other");
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert(typeof d === "string" ? d : (d?.message || "Yükleme başarısız"));
    } finally { setBusy(false); }
  };

  const del = async (aid) => {
    if (!window.confirm("Bu kanıtı silmek istediğinizden emin misiniz?")) return;
    try {
      await api.del(`/gmp/evidence-file/${aid}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Silme başarısız");
    }
  };

  const openPreview = async (aid, name) => {
    try {
      const resp = await fetch(`${API}/gmp/evidence-file/${aid}/download?inline=true`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      if (!resp.ok) throw new Error("Önizleme alınamadı");
      const blob = await resp.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { alert(e.message); }
  };

  const download = async (aid) => {
    try {
      const resp = await fetch(`${API}/gmp/evidence-file/${aid}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = data.attachments.find(x => x.id === aid)?.filename || aid;
      a.click();
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="border rounded p-3 bg-amber-50/40 border-amber-200" data-testid={`gmp-evidence-${module}-${recordId}`}>
      <div className="flex items-center gap-2 text-xs font-semibold text-amber-800 mb-2">
        <FileText size={13}/> {title || "Ekler / Kanıtlar"} ({data.count || 0})
      </div>

      {data.attachments.length > 0 && (
        <ul className="space-y-1 mb-2">
          {data.attachments.map(a => (
            <li key={a.id} className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1" data-testid={`gmp-evid-row-${a.id}`}>
              <span className="flex-1 truncate">
                <span className="font-medium">{a.filename}</span>
                <span className="text-slate-400 ml-2">· {KIND_LABELS[a.kind] || a.kind}</span>
                {a.description && <span className="text-slate-500 ml-1">— {a.description}</span>}
              </span>
              <span className="text-[10px] text-slate-400 font-mono" title={a.uploaded_at}>
                {Math.round((a.size || 0) / 1024)} KB · {a.uploaded_by_name}
              </span>
              <button onClick={() => openPreview(a.id, a.filename)}
                      className="text-indigo-600 hover:bg-indigo-50 p-0.5 rounded"
                      title="Önizle"
                      data-testid={`gmp-evid-preview-${a.id}`}>
                <Eye size={11}/>
              </button>
              <button onClick={() => download(a.id)}
                      className="text-sky-600 hover:bg-sky-50 p-0.5 rounded"
                      title="İndir"
                      data-testid={`gmp-evid-dl-${a.id}`}>
                <Download size={11}/>
              </button>
              {data.can_manage && (
                <button onClick={() => del(a.id)}
                        className="text-red-600 hover:bg-red-50 p-0.5 rounded"
                        title="Sil"
                        data-testid={`gmp-evid-del-${a.id}`}>
                  <Trash2 size={11}/>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-6 gap-1 items-end text-xs">
        <select value={kind} onChange={e => setKind(e.target.value)}
                className="border border-slate-300 rounded px-1 py-1 col-span-2"
                data-testid={`gmp-evid-kind-${module}-${recordId}`}>
          {Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input value={desc} onChange={e => setDesc(e.target.value)}
               placeholder="Açıklama (opsiyonel)"
               className="border border-slate-300 rounded px-2 py-1 col-span-2"/>
        <input type="file" onChange={e => setFile(e.target.files?.[0] || null)}
               className="col-span-1 text-[10px]"
               data-testid={`gmp-evid-file-${module}-${recordId}`}/>
        <Btn size="sm" onClick={upload} disabled={busy || !file}
             data-testid={`gmp-evid-upload-${module}-${recordId}`}>
          {busy ? <Loader2 size={11} className="animate-spin"/> : <Upload size={11}/>}
          Yükle
        </Btn>
      </div>
    </div>
  );
}
